import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

/* ---------------- FETCH ALL ---------------- */
export const fetchClients = createAsyncThunk(
  "client/fetchClients",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/client-codes`);
      return response.data; 
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch clients";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- CREATE ---------------- */
export const createClient = createAsyncThunk(
  "client/createClient",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      // Mapping logic to match the required API payload
      const response = await axios.post(`${API_URL}/client-codes`, {
        name: data.clientName,           // UI 'clientName' maps to API 'name'
        clientCode: data.clientCode,
        details: data.details,
        address: data.address,
        mobile: data.mobile,
        email: data.email,
        contactPerson: data.contactPerson,
        website: data.website,
        status: data.isActive ? "Active" : "Inactive", 
      });
      
      dispatch(fetchClients());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create client";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- UPDATE ---------------- */
export const updateClient = createAsyncThunk(
  "client/updateClient",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/client-codes/${id}`, {
        name: data.clientName,           // UI 'clientName' maps to API 'name'
        clientCode: data.clientCode,
        details: data.details,
        address: data.address,
        mobile: data.mobile,
        email: data.email,
        contactPerson: data.contactPerson,
        website: data.website,
        status: data.isActive ? "Active" : "Inactive",
      });
      
      dispatch(fetchClients());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update client";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- TOGGLE STATUS ---------------- */
export const toggleClientStatus = createAsyncThunk(
  "client/toggleStatus",
  async ({ id, currentStatus }, { dispatch, rejectWithValue }) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      
      const response = await axios.patch(`${API_URL}/client-codes/${id}`, {
        status: newStatus,
      });
      
      dispatch(fetchClients());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to toggle status";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

const clientSlice = createSlice({
  name: "client",
  initialState: {
    list: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearClientError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => { state.status = "loading"; })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(createClient.pending, (state) => { state.status = "loading"; })
      .addCase(createClient.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(updateClient.pending, (state) => { state.status = "loading"; })
      .addCase(updateClient.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearClientError } = clientSlice.actions;
export default clientSlice.reducer;